<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { useConfirm } from '@/composables/useConfirm';

const { state, answer } = useConfirm();

// The text box for confirm.prompt(). Reset to the caller's starting text each
// time a dialog opens, and focused so the user can type at once.
const text = ref('');
const inputEl = ref<HTMLInputElement | null>(null);
const dialogEl = ref<HTMLElement | null>(null);
watch(() => state.options, (opts) => {
  if (!opts) return;
  text.value = opts.inputValue ?? '';
  if (opts.inputLabel) void nextTick(() => inputEl.value?.focus());
});

function onKey(e: KeyboardEvent): void {
  if (!state.open || e.isComposing) return;
  if (e.key === 'Escape') answer(false);
  // On a focused button INSIDE the dialog, Enter is that button's own click
  // (Cancel must cancel). Anywhere else (a trigger button outside the dialog,
  // the input, the page) Enter confirms.
  const inDialogButton = e.target instanceof HTMLButtonElement && !!dialogEl.value?.contains(e.target);
  if (e.key === 'Enter' && !inDialogButton) {
    // Stop the browser's own Enter-click on a focused trigger button: it would
    // re-open the dialog we are closing.
    e.preventDefault();
    answer(true, text.value);
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', onKey);
}
</script>

<template>
  <Teleport to="body">
    <div v-if="state.open" class="modal-backdrop" data-test-id="confirm-host" @click.self="answer(false)">
      <div ref="dialogEl" class="confirm">
        <h2 class="confirm__title">{{ state.options?.title }}</h2>
        <!-- pre-line, because a consent notice that lists what an action
             destroys needs its list to stay a list. wiring.css is copied
             verbatim from the prototype, so the rule lives here instead.
             Bodies with no newline are unaffected. -->
        <p v-if="state.options?.body" class="confirm__body" style="white-space: pre-line">{{ state.options.body }}</p>
        <label
          v-if="state.options?.inputLabel"
          class="confirm__body"
          style="display: grid; gap: 4px; font-weight: 600"
        >
          {{ state.options.inputLabel }}
          <input
            ref="inputEl"
            v-model="text"
            type="text"
            data-test-id="confirm-input"
            style="padding: 8px 10px; border: 1px solid var(--border-subtle); border-radius: var(--radius-sm); font: inherit; font-weight: 400; color: var(--fg1); background: var(--surface-card)"
          />
        </label>
        <div class="confirm__actions">
          <button class="btn" data-test-id="confirm-cancel" @click="answer(false)">
            {{ state.options?.cancelLabel ?? 'Cancel' }}
          </button>
          <button
            class="btn btn--primary"
            :style="state.options?.danger ? 'background: var(--color-red); border-color: var(--color-red);' : ''"
            data-test-id="confirm-ok"
            @click="answer(true, text)"
          >
            {{ state.options?.confirmLabel ?? 'Confirm' }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
